/**
 * The PostgREST access token, and how it is obtained.
 *
 * TWO TOKENS, DELIBERATELY NOT ONE.
 *
 *   Keycloak token  proves who the user is
 *   PostgREST token says which Postgres ROLE the request runs as
 *
 * They are not interchangeable and must not be conflated. PostgREST reads its
 * token's `role` claim and does SET LOCAL ROLE; the role's grants then decide
 * which schema is reachable. Handing PostgREST a Keycloak token would either
 * fail (wrong signing key) or, worse, succeed with whatever `role` a claim
 * happened to carry.
 *
 * So the portal mints the second one: it verifies the Keycloak token against
 * JWKS, resolves the user's organization server-side, and signs a short-lived
 * token naming exactly that org's role. The browser never chooses its own
 * role — if it forges one, the signature fails; if it forges the
 * Accept-Profile header instead, Postgres answers `permission denied for
 * schema`, which is the boundary this whole design rests on.
 *
 * Kept in memory only. sessionStorage would survive a tab crash but also
 * survive a logout in another tab, and this token is cheap to re-mint.
 */

import { getCrmConfig } from "@/lib/crmConfig";

type Minted = {
  token: string;
  /** Postgres role, e.g. "crm_org_9" — also the PostgREST schema name. */
  role: string;
  organizationId: number;
  /**
   * This user's row in the org's `sales` table — a BIGINT, and the id
   * react-admin must report as the identity.
   *
   * atomic-crm stamps `sales_id` on everything a user creates and reads it
   * from `getIdentity().id`. Reporting the Keycloak subject there put a UUID
   * into a bigint column: `invalid input syntax for type bigint`. Only the
   * portal can map one to the other, so it travels with the token.
   */
  salesId: string;
  /** Epoch ms. */
  expiresAt: number;
};

let current: Minted | null = null;
let inFlight: Promise<Minted> | null = null;

/** Re-mint this long before expiry, so a request never races the deadline. */
const REFRESH_MARGIN_MS = 60_000;

const decodeExp = (jwt: string): number => {
  try {
    const [, payload] = jwt.split(".");
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof json.exp === "number" ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

/**
 * Exchange the Keycloak access token for a PostgREST token.
 *
 * `keycloakToken` is supplied by the auth provider; this module deliberately
 * does not reach into auth state, so the exchange is testable on its own.
 */
export const mintToken = async (keycloakToken: string): Promise<Minted> => {
  const cfg = await getCrmConfig();
  const res = await fetch(`${cfg.portalUrl}/api/crm/token`, {
    method: "POST",
    headers: { Authorization: `Bearer ${keycloakToken}` },
    credentials: "omit",
  });
  if (!res.ok) {
    // 403 here means authenticated but no CRM for this organization — a
    // different situation from "not logged in", and the UI should say so
    // rather than bouncing the user back to a login page that will succeed
    // and land them here again.
    throw new Error(
      res.status === 403
        ? "no CRM is enabled for your organization"
        : `could not obtain a CRM token: HTTP ${res.status}`,
    );
  }
  const body = await res.json();
  if (!body.token || !body.role) {
    throw new Error("token endpoint returned no token/role");
  }
  if (!body.sales_id) {
    // Without it every create fails on a type error deep in PostgREST, which
    // reads as a broken database rather than a missing profile.
    throw new Error("token endpoint returned no sales_id");
  }
  return {
    token: body.token,
    role: body.role,
    organizationId: body.organization_id,
    salesId: String(body.sales_id),
    expiresAt: decodeExp(body.token),
  };
};

/**
 * The current token, minting or refreshing as needed.
 *
 * Concurrent callers share one in-flight request: react-admin fires several
 * queries on first paint, and without this the portal would see a burst of
 * identical mint requests for the same user.
 */
export const getToken = async (
  keycloakToken: () => Promise<string>,
): Promise<Minted> => {
  if (current && Date.now() < current.expiresAt - REFRESH_MARGIN_MS) {
    return current;
  }
  if (!inFlight) {
    inFlight = keycloakToken()
      .then(mintToken)
      .then((m) => {
        current = m;
        return m;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export const clearToken = () => {
  current = null;
  inFlight = null;
};

/** Test seam. */
export const peekToken = () => current;
